import React from 'react';
import { AlertTriangle, AlertCircle } from 'lucide-react';
import { Interpretation, isCritical } from '@/lib/vitals/ranges';

export interface AbnormalEntry {
  label: string;
  valueText: string;
  interpretation: Interpretation;
}

interface AbnormalSummaryProps {
  entries: AbnormalEntry[];
}

const interpretationText = (i: Interpretation): string => {
  switch (i) {
    case 'LL': return 'critical low';
    case 'HH': return 'critical high';
    case 'L': return 'low';
    case 'H': return 'high';
    case 'N': return 'normal';
  }
};

const AbnormalSummary: React.FC<AbnormalSummaryProps> = ({ entries }) => {
  const critical = entries.filter(e => isCritical(e.interpretation));
  const abnormal = entries.filter(e => !isCritical(e.interpretation));

  if (entries.length === 0) return null;

  if (critical.length > 0) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-3">
        <div className="flex items-start gap-2">
          <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-red-900">
              {critical.length} critical value{critical.length === 1 ? '' : 's'} — confirm before saving
            </div>
            <ul className="mt-1.5 space-y-0.5 text-sm text-red-800">
              {critical.map((e, i) => (
                <li key={i}>
                  <span className="font-medium">{e.label}:</span> {e.valueText} ({interpretationText(e.interpretation)})
                </li>
              ))}
              {abnormal.map((e, i) => (
                <li key={`a-${i}`} className="text-red-700/80">
                  <span className="font-medium">{e.label}:</span> {e.valueText} ({interpretationText(e.interpretation)})
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-amber-900">
            {abnormal.length} value{abnormal.length === 1 ? '' : 's'} outside normal range — review before saving
          </div>
          <ul className="mt-1.5 space-y-0.5 text-sm text-amber-800">
            {abnormal.map((e, i) => (
              <li key={i}>
                <span className="font-medium">{e.label}:</span> {e.valueText} ({interpretationText(e.interpretation)})
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AbnormalSummary;
